import { useEffect } from "react";

/**
 * Fire callback when the document becomes visible after being hidden
 * (tab switch, app foreground from background on mobile/PWA).
 *
 * Use this to refetch stale data and revive WS connections that may have
 * been killed by iOS while the app was suspended.
 *
 * Optional `minHiddenMs`: only fire if the hide period was at least this
 * long, to avoid refetching on every transient blur.
 */
export default function useVisibility(onForeground, { minHiddenMs = 0 } = {}) {
  useEffect(() => {
    let hiddenAt = null;
    const onChange = () => {
      if (document.hidden) {
        hiddenAt = Date.now();
      } else {
        const elapsed = hiddenAt ? Date.now() - hiddenAt : 0;
        hiddenAt = null;
        if (elapsed >= minHiddenMs) {
          try {
            onForeground?.(elapsed);
          } catch (e) {
            console.error(e);
          }
        }
      }
    };
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  }, [onForeground, minHiddenMs]);
}
